import React, { useRef, useEffect, useState } from 'react'

export type SortState = 'inactive' | 'desc' | 'asc'

function plainLabel(label: string): string {
  return label.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').trim()
}

function sortActionHint(sortState: SortState): string {
  // Matches TanStack toggle cycle: false → asc → desc → false
  if (sortState === 'inactive') return 'double tap to sort ascending'
  if (sortState === 'asc') return 'double tap to sort descending'
  return 'double tap to remove sort'
}

function sortStateLabel(sortState: SortState): string {
  if (sortState === 'asc') return 'sorted ascending'
  if (sortState === 'desc') return 'sorted descending'
  return 'not sorted'
}

export default function SortHeader({
  label,
  sortState,
  onClick
}: {
  label: string
  sortState: SortState
  onClick?: (e: React.MouseEvent) => void
}) {
  const textRef = useRef<HTMLSpanElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldScroll, setShouldScroll] = useState(false)
  const accessibleLabel = plainLabel(label)

  // SVGs for each state
  const iconInactive = (
    <svg xmlns='http://www.w3.org/2000/svg' width='9' height='8' viewBox='0 0 9 8' fill='none' aria-hidden='true'>
      <path
        d='M5.95826 3.08301H3.03914C2.59369 3.08301 2.3706 2.54444 2.68558 2.22946L4.14514 0.769894C4.34041 0.574632 4.65699 0.574632 4.85225 0.769894L6.31181 2.22945C6.62679 2.54444 6.40371 3.08301 5.95826 3.08301Z'
        fill='#B2B2B2'
      />
      <path
        d='M3.04174 4.83301L5.96086 4.83301C6.40631 4.83301 6.6294 5.37158 6.31441 5.68656L4.85486 7.14612C4.65959 7.34138 4.34301 7.34138 4.14775 7.14612L2.68819 5.68656C2.37321 5.37158 2.59629 4.83301 3.04174 4.83301Z'
        fill='#B2B2B2'
      />
    </svg>
  )
  const iconAsc = (
    <svg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path
        d='M7.12631 3.4165H2.87385C2.4284 3.4165 2.20532 2.87793 2.5203 2.56295L4.64653 0.436724C4.84179 0.241462 5.15837 0.241461 5.35363 0.436723L7.47986 2.56295C7.79484 2.87793 7.57176 3.4165 7.12631 3.4165Z'
        fill='#9DBFE0'
      />
      <path
        d='M2.87369 6.0415L7.12615 6.0415C7.5716 6.0415 7.79468 6.58007 7.4797 6.89506L5.35347 9.02128C5.15821 9.21655 4.84163 9.21655 4.64637 9.02128L2.52014 6.89506C2.20516 6.58007 2.42824 6.0415 2.87369 6.0415Z'
        fill='#095EB2'
      />
    </svg>
  )
  const iconDesc = (
    <svg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path
        d='M7.12631 3.4165H2.87385C2.4284 3.4165 2.20532 2.87793 2.5203 2.56295L4.64653 0.436724C4.84179 0.241462 5.15837 0.241461 5.35363 0.436723L7.47986 2.56295C7.79484 2.87793 7.57176 3.4165 7.12631 3.4165Z'
        fill='#095EB2'
      />
      <path
        d='M2.87369 6.0415L7.12615 6.0415C7.5716 6.0415 7.79468 6.58007 7.4797 6.89506L5.35347 9.02128C5.15821 9.21655 4.84163 9.21655 4.64637 9.02128L2.52014 6.89506C2.20516 6.58007 2.42824 6.0415 2.87369 6.0415Z'
        fill='#9DBFE0'
      />
    </svg>
  )

  let labelClass = 'font-matter text-[10px] text-center whitespace-nowrap font-normal text-grey40'
  let icon = iconInactive
  if (sortState === 'desc') {
    labelClass = 'font-matter text-[10px] text-center whitespace-nowrap font-medium text-primary-blue-20'
    icon = iconDesc
  } else if (sortState === 'asc') {
    labelClass = 'font-matter text-[10px] text-center whitespace-nowrap font-medium text-primary-blue-20'
    icon = iconAsc
  }

  useEffect(() => {
    const measure = () => {
      if (!textRef.current || !containerRef.current) return
      const text = textRef.current
      const container = containerRef.current

      // Create a temporary element to measure the actual text width
      const tempElement = document.createElement('span')
      tempElement.style.visibility = 'hidden'
      tempElement.style.position = 'absolute'
      tempElement.style.whiteSpace = 'nowrap'
      tempElement.className = labelClass
      tempElement.textContent = label
      document.body.appendChild(tempElement)

      const textWidth = tempElement.offsetWidth
      const containerWidth = container.clientWidth
      const overflow = textWidth - containerWidth

      // Clean up the temporary element
      document.body.removeChild(tempElement)

      if (overflow > 0) {
        setShouldScroll(true)
        text.style.width = `${textWidth}px`
        text.style.setProperty('--scroll-distance', `${overflow}px`)
      } else {
        setShouldScroll(false)
        text.style.width = ''
        text.style.removeProperty('--scroll-distance')
      }
    }

    measure()

    // Re-measure on container resize
    const ro = new ResizeObserver(() => measure())
    if (containerRef.current) ro.observe(containerRef.current)
    if (textRef.current) ro.observe(textRef.current)

    // Re-measure after fonts load
    if ((document as any).fonts && typeof (document as any).fonts.ready?.then === 'function') {
      ;(document as any).fonts.ready.then(() => measure())
    }

    // Window resize
    window.addEventListener('resize', measure)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [label, labelClass])

  // Power Select_B20: native button + sort action in accessible name (TalkBack hint)
  const ariaLabel = `${accessibleLabel}, ${sortStateLabel(sortState)}, ${sortActionHint(sortState)}`

  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={ariaLabel}
      className='flex flex-row items-center justify-center gap-[2px] cursor-pointer select-none mb-2 border-none bg-transparent p-0 w-full'
    >
      <div ref={containerRef} className='relative overflow-hidden' aria-hidden='true'>
        <span ref={textRef} className={`${labelClass} ${shouldScroll ? 'scroll-text' : ''}`}>
          {label}
        </span>
        {shouldScroll && (
          <div className='pointer-events-none absolute top-0 right-0 h-full w-2 bg-gradient-to-l from-white to-transparent' />
        )}
      </div>
      <span className='flex-shrink-0 mt-[6px]' aria-hidden='true'>
        {icon}
      </span>
    </button>
  )
}
